import { supabase } from '../../lib/supabaseClient';

export interface UserRow {
  id: string;
  name: string | null;
  email: string | null;
  role: string | null;
  organization_id: string | null;
  updated_at: string | null;
  department_id: string | null;
  role_id: string | null;
}

export interface DepartmentRow {
  id: string;
  name: string;
  color?: string;
}

export const organizationStructureService = {
  async fetchOrgMembers(organizationId: string): Promise<UserRow[]> {
    if (!organizationId) return [];

    const { data: memberships, error } = await supabase
      .from('user_organizations')
      .select('user_id, role, organization_id, department_id, role_id, created_at')
      .eq('organization_id', organizationId)
      .limit(100);

    if (error) throw error;
    if (!memberships?.length) return [];

    const userIds = memberships.map(m => m.user_id).filter(Boolean);
    const { data: profiles, error: profileErr } = await supabase
      .from('user_profiles')
      .select('user_id, name, email, updated_at')
      .in('user_id', userIds)
      .limit(100);

    if (profileErr) throw profileErr;
    const profileMap = new Map(profiles?.map(p => [String(p.user_id), p]));

    return memberships.map(m => {
      const profile = profileMap.get(String(m.user_id));
      return {
        id: String(m.user_id),
        name: profile?.name ?? null,
        email: profile?.email ?? null,
        role: m.role ?? null,
        organization_id: m.organization_id ?? organizationId,
        updated_at: profile?.updated_at ?? m.created_at ?? null,
        department_id: m.department_id ?? null,
        role_id: m.role_id ?? null,
      };
    });
  },

  async fetchDepartments(organizationId: string): Promise<DepartmentRow[]> {
    if (!organizationId) return [];
    
    const { data, error } = await supabase
      .from('org_departments')
      .select('id, name, color')
      .eq('organization_id', organizationId);
      
    if (error) throw error;
    return data || [];
  },

  async updateUserRole(organizationId: string, userId: string, newRole: string): Promise<void> {
    if (!organizationId || !userId || !newRole) return;
    const { error } = await supabase
      .from('user_organizations')
      .update({ role: newRole })
      .eq('organization_id', organizationId)
      .eq('user_id', userId);
    if (error) throw error;
  },

  async removeMember(organizationId: string, userId: string): Promise<void> {
    if (!organizationId || !userId) return;
    const { error } = await supabase
      .from('user_organizations')
      .delete()
      .eq('organization_id', organizationId)
      .eq('user_id', userId);
    if (error) throw error;
  }
};
